import { useCallback, useEffect, useRef } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { notice } from '@/components/pbo/notice'

/** 1.0 공통 0건 문구(E15)와 같은 문구. */
export const SEARCH_EMPTY_MESSAGE = '조회된 결과가 존재하지 않습니다.'

/**
 * RP12·RP13 헤더 조회가 0건이면 1.0 처럼 안내한다.
 *
 * [조회]로 연 조회 번호만 기억해 두고, 그 조회가 성공으로 끝났을 때 한 번만 띄운다.
 * URL 복원 진입의 자동 조회는 누른 적 없는 안내가 되므로 기억하지 않는다.
 * 실패 알럿은 전역(queryClient)이 띄우므로 실패로 끝나면 조용히 지운다.
 */
export function useEmptySearchNotice(seq: number | undefined, query: Pick<UseQueryResult<unknown[], Error>, 'data' | 'isFetching' | 'status'>): (searchSeq: number) => void {
  const pendingSeqRef = useRef<number | null>(null)
  const { data, isFetching, status } = query

  useEffect(() => {
    if (seq == null || seq !== pendingSeqRef.current) return
    if (isFetching || status === 'pending') return
    pendingSeqRef.current = null
    if (status === 'success' && data?.length === 0) notice.invalid(SEARCH_EMPTY_MESSAGE)
  }, [data, isFetching, seq, status])

  return useCallback((searchSeq: number) => {
    pendingSeqRef.current = searchSeq
  }, [])
}
